/**
 * Copyright (c) 2021, Ouster, Inc.
 * All rights reserved.
 */
// import { Box, VizFooter } from '@ouster/webviz-react-components';

import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';

import { ReactComponent as StudioLogo } from '../images/Studio-Dark.svg';

/// ///////////////////////////////////////////////////////////////////////////
// Navbar Component
/// ///////////////////////////////////////////////////////////////////////////

// Helper component for a centered full height nav item
const NavFullHeightContainer = styled.div`
  display: grid;
  place-items: center;
  height: 100%;
  font-size: 15px;
`;

// Row of elements with consistent spacing for navs
const NavFlexRow = styled.div<{ gap?: string }>`
  display: flex;
  align-items: center;
  gap: ${(props) => props.gap ?? '10px'};
`;

const Nav = styled.nav<{ numElements?: number }>`
  /* Inside a flex column, make sure this navbar will keep its height */
  flex: 0 0 auto;

  /* 
    Adjust here to match the public page new design
  */
  height: 64px;
  background-color: var(--ouster-colors-background-main);
  border-bottom: 1px solid var(--color-unify-white-30);
  align-items: center;

  color: var(--color-milky-way);
  padding: 0 8px;

  /* Ensure the middle of the three elements is actually centered */
  display: grid;
  /* 
    If there are two elements in the nav bar, they should be spread to both ends
  */
  grid-template-columns: ${({ numElements }) =>
    numElements === 1 ? 'auto auto 1fr' : '1fr auto 1fr'};

  & > *:last-child {
    justify-self: flex-end;
  }

  /* Keep gap so that the elements aren't too close to each other on
  small screen sizes */
  gap: 32px;

  /* Allow for popup menus */
  position: relative;
`;

const OusterLogo = styled(Link)<{ disabled: boolean }>`
  width: 138px;
  min-width: 138px;
  padding-left: 6px;

  ${(props) => props.disabled && 'pointer-events: none;'}
`;

type Props = {
  centerElement?: JSX.Element;
  rightElement?: JSX.Element;
};
const NavBar = React.memo((props: Props) => {
  const { centerElement, rightElement } = props;
  const numElements = [centerElement, rightElement].filter(Boolean).length;

  const hasKcToken = Boolean(localStorage.getItem('kctoken'));

  return (
    <Nav numElements={numElements}>
      {/* 
        non-authenticated user, send to landing page '/'
        authenticated user, send to drive page
      */}
      <OusterLogo to={hasKcToken ? '/drive' : '/public'} disabled={false}>
        {/* <OusterSvgLogo /> */}
        <StudioLogo width={130} />
      </OusterLogo>
      {centerElement || <div />}
      {rightElement || <div />}
    </Nav>
  );
});

/// ///////////////////////////////////////////////////////////////////////////
// Helper components
/// ///////////////////////////////////////////////////////////////////////////

/**
 * Helper styled-component container that users of the Navbar component
 * can decide to add to the DOM after the NavBar inside a flex column
 */
const ContainerBelowNav = styled.div`
  width: 100%;
  height: 100%;

  /* Inside a flex column, the content should take the rest of the
 height of the page but not overflow */
  flex: 1 1 auto;
  /* Override min-width of auto so it can shrink below its potentially
 overflowing content and stay within the page */
  min-height: 0;
  display: flex;
  flex-direction: column;
`;

/**
 * Element which can be placed inside a flex column div, whose children
 * will be positioned below a navbar
 */
const FlexColumnWithNav: React.FC<{
  navCenterEl?: JSX.Element;
  navRightEl?: JSX.Element;
  children?: React.ReactNode;
}> = ({ navCenterEl, navRightEl, children }) => (
  <>
    <NavBar centerElement={navCenterEl} rightElement={navRightEl} />
    <ContainerBelowNav>{children}</ContainerBelowNav>
  </>
);

export {
  NavBar,
  ContainerBelowNav,
  FlexColumnWithNav,
  NavFlexRow,
  NavFullHeightContainer,
};
